import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import EvaluationPlanDeleteHoldButton from '../evaluationPlan/EvaluationPlanDeleteHoldButton';
import type { TodoShareBoardMeta } from '../../services/todoShareService';
import { getTodoSharePublicUrl } from '../../services/todoShareService';
import {
  TODO_SHARE_TITLE_SUFFIX,
  isTodoShareDefaultBoard,
  todoShareNameFromTitle,
} from '../../utils/todoShareTitle';

export type TodoShareBoardEditModalProps = {
  open: boolean;
  board: TodoShareBoardMeta | null;
  busy?: boolean;
  error?: string;
  onClose: () => void;
  onCopyLink: () => void;
  onToggleActive: () => void;
  onRename: (title: string) => void | Promise<void>;
  onRotateCode: (code: string) => void | Promise<void>;
  onDelete: () => void | Promise<void>;
};

function randomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 6; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

const TodoShareBoardEditModal: React.FC<TodoShareBoardEditModalProps> = ({
  open,
  board,
  busy = false,
  error = '',
  onClose,
  onCopyLink,
  onToggleActive,
  onRename,
  onRotateCode,
  onDelete,
}) => {
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !board) return;
    const fromDb = (board.entry_code ?? '').trim();
    setTitle(todoShareNameFromTitle(board.title));
    setCode(fromDb);
    setAppliedCode(fromDb);
    setCopied(false);
    setDeleteConfirmOpen(false);
  }, [open, board]);

  if (!open || !board) return null;

  const publicUrl = getTodoSharePublicUrl(board.public_token);
  const boardName = todoShareNameFromTitle(board.title);
  const nameLocked = isTodoShareDefaultBoard(board);
  const nameDirty = !nameLocked && title.trim() !== boardName;
  const trimmedCode = code.trim();
  const codeDirty = trimmedCode !== appliedCode.trim();
  const canApplyCode = codeDirty && trimmedCode.length >= 4;

  return createPortal(
    <div
      className="cp-todo-share-modal-overlay"
      onClick={() => {
        if (!busy && !deleteConfirmOpen) onClose();
      }}
      role="presentation"
    >
      <div
        className="cp-todo-share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-todo-share-edit-title"
        data-tutorial-todo="note-settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-todo-share-modal-header">
          <div className="cp-todo-share-modal-header-main">
            <h3 id="cp-todo-share-edit-title">노트 설정</h3>
            <div
              className={`cp-todo-share-active-toggle${board.is_active ? ' is-on' : ''}${
                busy ? ' is-disabled' : ''
              }`}
              role="switch"
              aria-checked={board.is_active}
              aria-label={board.is_active ? '공유 중' : '비공개'}
              data-tutorial-todo="share-toggle"
              tabIndex={busy ? -1 : 0}
              onClick={() => {
                if (!busy) onToggleActive();
              }}
              onKeyDown={(e) => {
                if (busy) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleActive();
                }
              }}
            >
              <span
                className={`cp-todo-share-active-toggle-label${
                  !board.is_active ? ' is-active' : ''
                }`}
              >
                비공개
              </span>
              <span
                className={`cp-todo-share-active-toggle-label${
                  board.is_active ? ' is-active' : ''
                }`}
              >
                공유
              </span>
            </div>
          </div>
          <div className="cp-todo-share-modal-header-actions">
            <EvaluationPlanDeleteHoldButton
              itemId={board.id}
              disabled={busy || nameLocked}
              onRemove={() => {
                if (!nameLocked) setDeleteConfirmOpen(true);
              }}
              className="cp-todo-share-delete-hold"
            />
            <button
              type="button"
              className="cp-todo-share-modal-close"
              onClick={onClose}
              aria-label="닫기"
              disabled={busy}
            >
              ×
            </button>
          </div>
        </div>

        <label className="cp-todo-share-modal-label">
          제목
          <div className="cp-todo-share-code-row">
            <div className={`cp-todo-share-title-field${nameLocked ? ' is-locked' : ''}`}>
              <input
                type="text"
                value={nameLocked ? boardName : title}
                maxLength={60}
                disabled={busy || nameLocked}
                readOnly={nameLocked}
                onChange={(e) => setTitle(e.target.value)}
                aria-describedby="cp-todo-share-edit-title-hint"
              />
              <span className="cp-todo-share-title-suffix" id="cp-todo-share-edit-title-hint">
                {TODO_SHARE_TITLE_SUFFIX}
              </span>
            </div>
            <button
              type="button"
              className="cp-btn-secondary cp-todo-share-side-btn"
              disabled={busy || nameLocked || !title.trim() || !nameDirty}
              title={nameLocked ? '기본 노트 이름은 변경할 수 없습니다' : undefined}
              onClick={() => {
                if (!nameLocked) void onRename(title.trim());
              }}
            >
              이름 저장
            </button>
          </div>
        </label>

        <div data-tutorial-todo="share-code">
          <label className="cp-todo-share-modal-label">
            입장코드
            <div className="cp-todo-share-code-row">
              <input
                type="text"
                value={code}
                maxLength={32}
                disabled={busy}
                placeholder={appliedCode ? undefined : '적용된 코드 없음 — 새 코드를 적용해 주세요'}
                onChange={(e) => setCode(e.target.value)}
              />
              <button
                type="button"
                className="cp-btn-secondary cp-todo-share-side-btn"
                disabled={busy}
                onClick={() => setCode(randomCode())}
              >
                코드 랜덤 생성
              </button>
            </div>
          </label>
          <button
            type="button"
            className="cp-btn-secondary cp-todo-share-modal-full cp-todo-share-accent-btn"
            disabled={busy || !canApplyCode}
            onClick={() => {
              void (async () => {
                const next = trimmedCode;
                try {
                  await onRotateCode(next);
                  setAppliedCode(next);
                  setCode(next);
                } catch {
                  /* 에러는 상위에서 표시 */
                }
              })();
            }}
          >
            {codeDirty ? '변경 코드 적용' : '코드 적용'}
          </button>
        </div>

        <div data-tutorial-todo="share-link">
          <div className="cp-todo-share-address-row">
            <span className="cp-todo-share-address-label">공유 링크 :</span>
            <a
              className="cp-todo-share-address-link"
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {publicUrl}
            </a>
          </div>

          <div className="cp-todo-share-modal-actions">
            <button
              type="button"
              className="cp-btn-secondary cp-todo-share-accent-btn"
              disabled={busy}
              onClick={() => {
                onCopyLink();
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1600);
              }}
            >
              {copied ? '복사됨' : '링크 복사'}
            </button>
          </div>
        </div>
        {error ? <p className="cp-todo-note-error">{error}</p> : null}

        {deleteConfirmOpen ? (
          <div className="cp-todo-share-delete-confirm" role="alertdialog" aria-modal="true">
            <p>
              「{todoShareNameFromTitle(board.title) || board.title}」 공유 보드를 삭제할까요?
              <br />
              삭제하면 복구할 수 없습니다.
            </p>
            <div className="cp-todo-share-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                disabled={busy}
                onClick={() => setDeleteConfirmOpen(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-save-all cp-todo-share-delete-confirm-btn"
                disabled={busy}
                onClick={() => void onDelete()}
              >
                {busy ? '삭제 중…' : '삭제'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default TodoShareBoardEditModal;
